import { useEffect, useMemo, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import ListGroup from 'react-bootstrap/ListGroup'
import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faChevronRight,
  faFolder,
  faFolderPlus,
  faHome,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons'
import {
  buildFolderTree,
  canHoldSubfolders,
  childFoldersOf,
  folderAncestors,
  folderPath
} from 'dashboard/folders/folder-tree'

const ROOT_LABEL = 'All Items'

// Filing into a folder takes FolderPolicy#move_item? — owner or shared editor. A read-only folder
// can still be opened, since a folder the user may write to can sit inside it.
const canFileInto = (folder) => !folder || folder.is_owner || folder.is_editor

// "Move to…" for a single item, in the shape of Google Drive's move dialog: where the item is now,
// a folder browser starting one level above it (so moving up a folder is one click on Move), and
// the full path of wherever it is about to go. The browser picks a folder; `onMove` does the
// moving, so each caller keeps its own move path (folders#move_item, or a report update).
//
// Clicking a row selects it, opening it (double-click or its chevron) browses into it. With
// nothing selected, the folder being browsed is the destination — as in Drive.
function MoveToFolderModal({
  show,
  onHide,
  itemName,
  currentFolderId,
  folders = [],
  onMove,
  isMoving = false,
  onCreateFolder
}) {
  const tree = useMemo(() => buildFolderTree(folders), [folders])
  const currentFolder = currentFolderId ? tree.byId.get(currentFolderId) || null : null

  // `browseId` null is the top level; `selectedId` undefined means nothing is picked in the list.
  const [browseId, setBrowseId] = useState(null)
  const [selectedId, setSelectedId] = useState(undefined)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (!show) return
    setBrowseId(currentFolder?.parent_id && tree.byId.has(currentFolder.parent_id) ? currentFolder.parent_id : null)
    setSelectedId(undefined)
    setCreating(false)
    setNewName('')
    // Only on open: re-running when the folder list refetches would throw away where the user browsed to.
  }, [show])

  const browsed = browseId ? tree.byId.get(browseId) || null : null
  const browsedAncestors = browsed ? folderAncestors(tree, browsed) : []
  const level = useMemo(
    () => [...childFoldersOf(tree, browseId)].sort((a, b) => a.name.localeCompare(b.name)),
    [tree, browseId]
  )

  const targetId = selectedId !== undefined ? selectedId : browseId
  const target = targetId ? tree.byId.get(targetId) || null : null
  const targetPath = target ? `${ROOT_LABEL} / ${folderPath(tree, target)}` : ROOT_LABEL
  const isCurrent = (targetId || null) === (currentFolderId || null)
  const canMove = !isMoving && !isCurrent && canFileInto(target)

  // A subfolder can be made where the user owns the folder (or at the top level), with depth to spare.
  const canCreateHere = !!onCreateFolder && (!browsed || browsed.is_owner) && canHoldSubfolders(tree, browsed)

  const browseInto = (folderId) => {
    setBrowseId(folderId)
    setSelectedId(undefined)
    setCreating(false)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (canMove) onMove(targetId || null)
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) return
    setIsCreating(true)
    try {
      const folder = await onCreateFolder({ name, parent_id: browseId })
      setCreating(false)
      setNewName('')
      if (folder?.id) setSelectedId(folder.id)
    } finally {
      setIsCreating(false)
    }
  }

  const footerNote = () => {
    if (isCurrent) return 'This is where it is now — pick another location'
    if (!canFileInto(target)) return "You can view this folder but can't move items into it"
    return null
  }

  return (
    <Modal show={show} onHide={onHide} centered onClick={(e) => e.stopPropagation()}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="h5 text-truncate" title={itemName}>Move “{itemName}”</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="text-muted small">Current location:</span>
            <Button
              variant="outline-secondary"
              size="sm"
              className="text-truncate"
              style={{ maxWidth: '20rem' }}
              title={currentFolder ? `${ROOT_LABEL} / ${folderPath(tree, currentFolder)}` : ROOT_LABEL}
              onClick={() => browseInto(currentFolder ? currentFolder.parent_id || null : null)}
              data-testid="move-current-location"
            >
              <FontAwesomeIcon icon={currentFolder ? faFolder : faHome} className="me-2" />
              {currentFolder ? currentFolder.name : ROOT_LABEL}
            </Button>
          </div>

          <div className="d-flex align-items-center gap-2 border-bottom pb-2 mb-2">
            <Button
              variant="link"
              size="sm"
              className="p-1 text-muted"
              aria-label="Back"
              disabled={!browsed}
              onClick={() => browseInto(browsed?.parent_id && tree.byId.has(browsed.parent_id) ? browsed.parent_id : null)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <Breadcrumb className="mb-0 small flex-grow-1" listProps={{ className: 'flex-wrap mb-0' }}>
              <Breadcrumb.Item active={!browsed} onClick={() => browseInto(null)} linkAs="span" style={{ cursor: 'pointer' }}>
                {ROOT_LABEL}
              </Breadcrumb.Item>
              {browsedAncestors.map((ancestor) => (
                <Breadcrumb.Item
                  key={ancestor.id}
                  onClick={() => browseInto(ancestor.id)}
                  linkAs="span"
                  style={{ cursor: 'pointer' }}
                >
                  {ancestor.name}
                </Breadcrumb.Item>
              ))}
              {browsed && <Breadcrumb.Item active>{browsed.name}</Breadcrumb.Item>}
            </Breadcrumb>
            {canCreateHere && !creating && (
              <Button
                variant="link"
                size="sm"
                className="p-1 text-muted"
                aria-label="New folder"
                title="New folder"
                onClick={() => setCreating(true)}
              >
                <FontAwesomeIcon icon={faFolderPlus} />
              </Button>
            )}
          </div>

          {creating && (
            <div className="d-flex gap-2 mb-2">
              <Form.Control
                autoFocus
                size="sm"
                value={newName}
                placeholder="New folder name"
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleCreate() }
                  if (e.key === 'Escape') { e.stopPropagation(); setCreating(false) }
                }}
              />
              <Button size="sm" variant="primary" onClick={handleCreate} disabled={!newName.trim() || isCreating}>
                {isCreating ? <Spinner size="sm" animation="border" /> : 'Create'}
              </Button>
              <Button size="sm" variant="light-primary" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          )}

          <ListGroup variant="flush" style={{ maxHeight: '18rem', overflowY: 'auto' }} data-testid="move-folder-list">
            {level.length === 0 && (
              <ListGroup.Item className="text-muted small border-0 py-4 text-center">
                No folders here
              </ListGroup.Item>
            )}
            {level.map((folder) => {
              const writable = canFileInto(folder)
              const isHere = folder.id === currentFolderId
              const hasChildren = childFoldersOf(tree, folder.id).length > 0
              return (
                <ListGroup.Item
                  key={folder.id}
                  action
                  active={selectedId === folder.id}
                  className="d-flex align-items-center gap-2 border-0 rounded py-2"
                  onClick={() => setSelectedId(folder.id)}
                  onDoubleClick={() => browseInto(folder.id)}
                  data-testid={`move-folder-${folder.id}`}
                >
                  <FontAwesomeIcon icon={faFolder} className={selectedId === folder.id ? '' : 'text-muted'} />
                  <span className={`text-truncate flex-grow-1 ${writable ? '' : 'opacity-50'}`}>{folder.name}</span>
                  {isHere && <small className="text-nowrap opacity-75">Current location</small>}
                  {!writable && !isHere && <small className="text-nowrap opacity-75">View only</small>}
                  {hasChildren && (
                    <Button
                      as="span"
                      variant="link"
                      size="sm"
                      role="button"
                      className={`p-1 ${selectedId === folder.id ? 'text-white' : 'text-muted'}`}
                      aria-label={`Open ${folder.name}`}
                      onClick={(e) => { e.stopPropagation(); browseInto(folder.id) }}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </Button>
                  )}
                </ListGroup.Item>
              )
            })}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer className="d-flex align-items-center">
          <div className="me-auto small text-muted text-truncate" style={{ maxWidth: '60%' }} data-testid="move-target-path">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            {footerNote() || <>Move to <span className="fw-semibold" title={targetPath}>{targetPath}</span></>}
          </div>
          <Button variant="light-primary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!canMove}>
            {isMoving ? <Spinner size="sm" animation="border" /> : 'Move'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default MoveToFolderModal
